/**
 * FGSD-001 · Global scroll behaviour
 *
 * Premium navigation standard (Founder-approved):
 *  - New navigation (PUSH / REPLACE)  → destination opens at the top of the page.
 *  - Browser Back / Forward (POP)     → previous scroll position is restored.
 *  - Hash links (#anchor)             → scroll to the anchored element.
 *
 * Mounted once inside <BrowserRouter>. Renders nothing.
 */
import { useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Take scroll handling away from the browser so POP restoration is deterministic.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const positions = new Map();

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();

  // Save the scroll position of the page being left. The cleanup closes
  // over THIS location's key and runs (as a layout-effect cleanup, before
  // the restore effect below scrolls the window) exactly when the route
  // changes away from it — capturing the departing page's position intact.
  useLayoutEffect(() => {
    const key = location.key;
    return () => {
      positions.set(key, window.scrollY);
    };
  }, [location.key]);

  useLayoutEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView({ block: 'start' });
        return;
      }
    }

    if (navType === 'POP' && positions.has(location.key)) {
      const y = positions.get(location.key);
      // Content below the fold (images, async sections) may not have laid
      // out yet, clamping the restore. Re-assert per frame (max ~0.5s)
      // only while the document is still too short — stop the moment the
      // position is reached so user scrolling is never hijacked.
      const tryRestore = (framesLeft) => {
        window.scrollTo(0, y);
        if (framesLeft > 0 && window.scrollY + 2 < y) {
          requestAnimationFrame(() => tryRestore(framesLeft - 1));
        }
      };
      tryRestore(30);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.key, location.hash, navType]);

  return null;
}
